import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { UserPreferencesService } from '~shared/services/preferences/user-preferences.service';

/** Clé de la préférence "ne plus m'avertir" dans le tableau `preference`. */
export const SKIP_UNSAVED_WARNING_KEY = 'skipUnsavedWarning';

/** Prédicat rendant l'état "modifié" d'une source (formulaire, vue, ...). */
export type DirtyCheck = () => boolean;

/**
 * Source unique de vérité sur les modifications non sauvegardées.
 *
 * Les écrans qui portent de la saisie (formulaires de record, configuration de
 * vue) s'enregistrent avec un prédicat `isDirty`. Les trois mécanismes de
 * protection interrogent ensuite ce service plutôt que de dupliquer la logique :
 *   - `unsavedChangesGuard` (CanDeactivate) pour la navigation Angular, ce qui
 *     couvre aussi les boutons précédent/suivant du navigateur (le `popstate`
 *     déclenche une navigation, donc le guard) ;
 *   - le listener `beforeunload` ci-dessous pour la fermeture d'onglet et le
 *     rechargement, que le routeur ne voit pas ;
 *   - les appels directs à `confirm()` pour les slide-ins, qui ne sont pas des
 *     routes et échappent donc au guard.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesService implements OnDestroy {
  private readonly preferences = inject(UserPreferencesService);
  private readonly sources = new Map<string, DirtyCheck>();
  private pendingResolve: ((proceed: boolean) => void) | null = null;

  /** Pilote l'affichage de la modale ; consommé par UnsavedChangesModalComponent. */
  readonly promptVisible = signal(false);

  constructor() {
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  }

  /**
   * Enregistre une source de modifications. Le même id ré-enregistré remplace
   * le prédicat précédent (remontage de composant).
   * @returns une fonction de désinscription, à appeler dans ngOnDestroy.
   */
  register(id: string, isDirty: DirtyCheck): () => void {
    this.sources.set(id, isDirty);
    return () => this.unregister(id);
  }

  unregister(id: string): void {
    this.sources.delete(id);
  }

  /** Vrai si au moins une source enregistrée est modifiée. */
  hasUnsavedChanges(): boolean {
    for (const isDirty of this.sources.values()) {
      // Un prédicat qui jette (composant à moitié détruit) ne doit pas bloquer
      // la navigation : on le considère propre.
      try {
        if (isDirty()) {
          return true;
        }
      } catch {
        continue;
      }
    }
    return false;
  }

  /** Vrai si l'utilisateur a coché "ne plus m'avertir". */
  isWarningDisabled(): boolean {
    return this.preferences.read<boolean>(SKIP_UNSAVED_WARNING_KEY) === true;
  }

  /**
   * Persiste le choix "ne plus m'avertir" (localStorage + PATCH /user).
   *
   * L'erreur est ignorée volontairement : l'utilisateur est en train de quitter
   * une page, faire échouer sa navigation sur un PATCH raté serait pire que le
   * problème. Le service de préférences annule alors l'écriture locale, donc
   * l'avertissement réapparaîtra — préférable à un réglage qu'on croit posé et
   * qui s'évapore au prochain login. Le réglage reste accessible dans
   * Paramètres > Préférences. La page de réglages, elle, remonte l'erreur.
   */
  disableWarning(): void {
    this.preferences
      .write(SKIP_UNSAVED_WARNING_KEY, true)
      .subscribe({ error: () => undefined });
  }

  /**
   * Demande confirmation avant d'abandonner les modifications.
   *
   * @param isDirty état à tester ; par défaut, l'agrégat des sources
   *   enregistrées. Les slide-ins passent leur propre prédicat, car ils sont
   *   plus fins que la page.
   * @returns `true` si l'on peut continuer (rien à perdre, avertissement
   *   désactivé, ou abandon confirmé), `false` si l'utilisateur veut rester.
   */
  confirm(isDirty?: DirtyCheck): Promise<boolean> {
    const dirty = isDirty ? isDirty() : this.hasUnsavedChanges();
    if (!dirty || this.isWarningDisabled()) {
      return Promise.resolve(true);
    }
    // Une seconde demande pendant qu'une modale est ouverte (double clic sur
    // "précédent") ne doit pas orpheliner la première promesse.
    if (this.pendingResolve) {
      this.pendingResolve(false);
      this.pendingResolve = null;
    }
    this.promptVisible.set(true);
    return new Promise<boolean>((resolve) => {
      this.pendingResolve = resolve;
    });
  }

  /**
   * L'utilisateur abandonne ses modifications.
   * @param skipFuture true si "ne plus m'avertir" était coché.
   */
  acceptDiscard(skipFuture: boolean): void {
    if (skipFuture) {
      this.disableWarning();
    }
    this.settle(true);
  }

  /** L'utilisateur reste sur la page. */
  rejectDiscard(): void {
    this.settle(false);
  }

  /** Marque une source comme sauvegardée/propre en la retirant de l'agrégat. */
  reset(id: string): void {
    this.unregister(id);
  }

  private settle(proceed: boolean): void {
    this.promptVisible.set(false);
    const resolve = this.pendingResolve;
    this.pendingResolve = null;
    resolve?.(proceed);
  }

  /**
   * Le message est imposé par le navigateur et non personnalisable — la case
   * "ne plus m'avertir" est donc honorée en n'armant tout simplement pas le
   * dialogue.
   */
  private readonly onBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (!this.hasUnsavedChanges() || this.isWarningDisabled()) {
      return;
    }
    event.preventDefault();
    // Chrome < 119 et Firefox ignorent preventDefault() seul et exigent que
    // returnValue soit renseigné pour afficher le dialogue.
    event.returnValue = '';
  };
}
